import * as a from '@mitranim/js/all.mjs'
import * as ob from '@mitranim/js/obs.mjs'
import * as c from './conf.mjs'
import * as um from './util-misc.mjs'
import * as ut from './util-text.mjs'
import * as uma from './util-math.mjs'
import * as uv from './util-view.mjs'
import * as ue from './util-err.mjs'
import * as uc from './util-coll.mjs'
import * as ucc from './util-coll-cls.mjs'
import * as uan from './util-analyt.mjs'

// Note: `window.env` is assigned in `client.mjs`.
export function getEnv() {
  if (c.DOM_AVAILABLE) return window.env
  throw Error(`unexpected access to window.env in SSR`)
}

export function getCtx() {return getEnv()?.ctx}

export function MixElemVisObs(cls) {
  a.reqCls(cls)
  if (!visObs) return cls

  return class MixElemVisObsCls extends cls {
    connectedCallback() {
      super.connectedCallback?.()
      visObs.observe(this)
    }

    disconnectedCallback() {
      visObs.unobserve(this)
      super.disconnectedCallback?.()
    }

    onVisChange(vis) {return a.laxBool(vis) ? this.onVisible() : this.onInvisible()}
    onVisible() {}
    onInvisible() {}
  }
}

export function MixElemVisibilityLazyLoad(cls) {
  a.reqCls(cls)

  return class MixElemVisibilityLazyLoadCls extends MixElemVisObs(cls) {
    onVisible() {
      if (this.loaded) return
      this.loaded = true
      uv.E(this, {}, this.loaderCls.init(this.onLazyInit()))
    }

    get loaderCls()  {throw ue.errImplementInSubclass()}
    async onLazyInit() {throw ue.errImplementInSubclass()}
  }
}

export function MixLazyMsgs(cls) {
  a.reqCls(cls)

  return class MixLazyMsgsCls extends uv.MixElemCtxSub(MixElemVisibilityLazyLoad(cls)) {
    preinit() {}

    async onLazyInit() {
      this.ctxReinit()
      await super.preinit()
      await this.pageDat().refresh()
      return await this.init()
    }
  }
}

/*
Any observed element must define the `onVisChange()` method. This method will be
called whenever the element's visibility changes, passing `true` if the element
just became visible.

TODO rename to "viewportObs" / "obsViewport".
*/
export const visObs = (
  a.vac(c.INTERSECTION_AVAILABLE) &&
  new IntersectionObserver(visObsOnChange, {threshold: 0.9})
)

function visObsOnChange(val) {
  for (const {target, isIntersecting} of val) {
    target.onVisChange(isIntersecting)
  }
}

export class InterObs extends (globalThis.IntersectionObserver || a.Emp) {
  constructor(ref, opt) {
    a.req(ref, isIntersector)
    super(ref.intersect.bind(ref), opt)
  }

  observe(val) {if (a.optNode(val)) super.observe(val)}
}

function isIntersector(val) {return a.hasMeth(val, `intersect`)}

// TODO better name.
export function intersectionEntryIsIntersectingWithViewportTop(val) {
  return (
    a.isStruct(val) &&
    val.isIntersecting &&
    val.intersectionRect.top === val.rootBounds.top &&
    val.boundingClientRect.top < val.rootBounds.top
  )
}

const ENABLE_ANALYT_OBS = c.INTERSECTION_AVAILABLE && (c.ENABLE_ACTS || c.GTAG_ENABLED)

/*
Sends analytics / tracking requests when a target element is visible.

In web advertising, the current standard for when something has been "viewed" is
being at least half-visible for at least a second. This class implements the 1s
delay by using cancelable timeouts.

TODO consider batching multiple timers and requests into one. Both our buket API
and the Gtag API for the `view_item` event support sending multiple viewed items
in one request.
*/
class AnalytViewObs extends (globalThis.IntersectionObserver || a.Emp) {
  constructor(...src) {
    super(...src)
    this.timers = new WeakMap()
    this.sent = new WeakSet()
  }

  unobserve(val) {
    super.unobserve(val)
    this.cancel(val)
  }

  cancel(val) {
    const timer = this.timers.get(val)
    if (timer) clearTimeout(timer)
  }

  intersect(src) {for (src of src) this.intersectOne(src)}

  intersectOne(src /* : IntersectionObserverEntry */) {
    const tar = src.target
    if (this.sent.has(tar)) return

    if (!src.isIntersecting) {
      this.cancel(tar)
      return
    }

    if (!uan.hasAnalytDataset(tar)) return

    const maybeSendView = () => {
      this.cancel(tar)

      if (!tar.isConnected) return
      this.sent.add(tar)

      analytViewSendAct(tar)
      analytViewSendGtag(tar)
    }

    this.timers.set(tar, setTimeout(maybeSendView, 1000))
  }
}

async function analytViewSendAct(tar) {
  const inp = uan.elemActInp(tar)
  if (!inp) return

  const n = await um.imp(import(`./net.mjs`))
  inp.type = c.ACT_TYPE_VIEW
  await n.createAct(getCtx(), inp)
}

/*
This doesn't hardcode a specific view event name, because in GA, UI kind of a
viewed entity is part of the event name. For comparison, in our acts, all
view acts have the type `view` but with different UI kinds.
*/
function analytViewSendGtag(tar) {
  const inp = uan.elemGtagInp(tar)
  if (!inp) return
  getEnv().gtag.sendEvent(...inp)
}

/*
Technical note. The callback passed to the intersection observer constructor
receives the observer both as the second argument and as `this`. In the
standard DOM API, passing `AnalytViewObs.prototype.intersect` is equivalent to
passing this function. However, some third party scripts, injected in some
users' browsers, override `IntersectionObserver` with their own
non-standard-compliant implementation which does _not_ provide the observer as
`this` when invoking the callback, breaking our code. Addendum: it seems that
some other 3rd party scripts have a differently non-compliant implementation,
failing to provide the observer as the second argument. So we check both
options.
*/
function viewObsIntersect(inp, obs) {
  obs = a.onlyInst(obs, AnalytViewObs) || a.onlyInst(this, AnalytViewObs) // eslint-disable-line no-invalid-this
  if (obs) obs.intersect(inp)
}

export const analytViewObsStrict = (
  a.vac(ENABLE_ANALYT_OBS) &&
  new AnalytViewObs(viewObsIntersect, {threshold: 0.5})
)

/*
TODO: an ideal implementation of a view observer for feeds would detect that the
user has viewed at least one row of items for at least a second. Seems tricky,
so for now, we skip the geometry check, but keep the timing check.
*/
export const analytViewObsLax = (
  a.vac(ENABLE_ANALYT_OBS) &&
  new AnalytViewObs(viewObsIntersect)
)

export class Loc extends a.Loc {
  replaceKey(key, val) {
    this.query.set(key, val)
    this.replace()
    return this
  }

  static replaceKey(key, val) {
    return this.current().replaceKey(key, val)
  }
}

export const CAPTURE = Object.freeze({capture: true})

export function* nodeWalk(src) {
  // Note: this ignores non-node values, instead of throwing, because the DOM
  // shim we use in SSR allows non-node primitives as node children, usually
  // strings and numbers. This is done for simplicity and performance.
  if (!a.isNode(src)) return
  yield src
  src = src.childNodes
  if (src) for (src of src) yield* nodeWalk(src)
}

export function nodeEach(node, fun) {
  a.reqFun(fun)
  for (node of nodeWalk(node)) fun(node)
}

export function nodesByTag(tag) {
  return a.arr(document.getElementsByTagName(a.reqStr(tag)))
}

export function attrSelector(key, val) {
  key = a.reqStr(key)
  if (a.isNil(val)) return a.str(`[`, key, `]`)

  val = JSON.stringify(a.laxStr(val))
  return a.str(`[`, key, `=`, val, `]`)
}

export function nodeByAttr(src, key, val) {
  return sel(src, attrSelector(key, val))
}

export function nodesByAttr(src, key, val) {
  return selAll(src, attrSelector(key, val))
}

export function selAttrVals(src, key) {
  return a.mapCompact(
    a.optElement(src)?.querySelectorAll(attrSelector(key)),
    function getAttr(val) {return val.getAttribute(key)},
  )
}

export function isDescendantOf(des, anc) {return isAncestorOf(anc, des)}

export function isAncestorOf(maybeAncestor, maybeDescendant) {
  return (
    a.isNode(maybeAncestor) &&
    a.isNode(maybeDescendant) && (
      maybeAncestor === maybeDescendant ||
      isAncestorOf(maybeAncestor, maybeDescendant.parentNode)
    )
  )
}

export function hasAncestor(tar, fun) {return !!a.findAncestor(tar, fun)}

export function findAncestorBetween(desc, anc, fun) {
  a.optNode(desc)
  a.optNode(anc)
  a.reqFun(fun)

  while (desc) {
    if (fun(desc)) return desc
    if (desc === anc) break
    const par = desc.parentNode
    if (par === desc) break // Should never happen.
    desc = par
  }
  return undefined
}

// TODO consider moving to `@mitranim/js`.
export function instChild(src, cls) {
  return uc.findInst(a.optElement(src)?.childNodes, cls)
}

// TODO consider moving to `@mitranim/js`.
export function instNextSibling(tar, cls) {
  a.reqCls(cls)
  return a.findNextSibling(tar, function test(val) {return a.isInst(val, cls)})
}

// TODO consider moving to `@mitranim/js`.
export function instPrevSibling(tar, cls) {
  a.reqCls(cls)
  return a.findPrevSibling(tar, function test(val) {return a.isInst(val, cls)})
}

export function addEvent(target, name, handler, opts) {
  target.addEventListener(name, handler, opts)

  return function removeEvent() {
    target.removeEventListener(name, handler, opts)
  }
}

/*
Sets `input.value` while rejecting invalid values such as objects.

Known issue: doesn't preserve the cursor position. When modifying the input
value produced by the user and replacing it with something else, properly
preventing cursor movement may require access to the previous value and to the
selection from before the last input event.

TODO see if `document.execCommand('insertText', ...)` can be a better solution.
*/
export function resetInputValue(input, val) {
  a.reqElement(input)
  val = a.renderLax(val)
  if (input.value !== val) input.value = val
}

export function selectElementContent(val) {
  if (a.isNil(val)) return
  a.reqElement(val)

  if (a.isFun(val.select)) {
    val.select()
  }
  else if (a.isFun(val.setSelectionRange)) {
    val.setSelectionRange(0, a.laxStr(val.value).length)
  }
}

export function focusElement(val) {
  if (a.isNil(val)) return
  a.reqElement(val)
  if (a.isFun(val.focus)) val.focus()
}

export function scrollYToId(selector, id) {
  scrollIntoView(document.getElementById(id))
}

export function scrollToTop() {
  window.scrollTo(window.scrollX, 0)
}

export function scrollYToVisible(id) {
  const elem = elemById(id)
  if (isKindaWithinViewport(elem)) return
  maybeScrollToElement(elem)
}

export function maybeScrollToElement(elem) {
  if (!c.DOM_AVAILABLE || !elem) return
  a.reqElement(elem)
  window.scrollTo(window.scrollX, elem.offsetTop)
}

export function preventScrollSpill(elem) {
  elem.addEventListener(`wheel`, scrollWithoutSpillAt, {passive: false})
}

function scrollWithoutSpillAt(event) {
  scrollWithoutSpill(this, event) // eslint-disable-line no-invalid-this
}

/*
Should be used on `wheel` events. Note that `scroll` events only happen when
there was space to scroll. When there's no space, `scroll` events silently spill
out without triggering the listener on that element. Also note that the `wheel`
listener requires `{passive: false}` in some recent browsers.

Reference: https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent.
*/
export function scrollWithoutSpill(elem, event) {
  if (event.deltaMode === WheelEvent.DOM_DELTA_PIXEL) {
    scrollWithoutSpillModePixels(elem, event)
  }
  else {
    scrollWithoutSpillModeLinesOrPages(elem, event)
  }
}

// Precise but requires `event.deltaMode === WheelEvent.DOM_DELTA_PIXEL`.
// Browsers use this mode for smooth-scroll devices such as touchpads. Some
// browsers, including Chrome, also use this for regular mouse wheels.
function scrollWithoutSpillModePixels(elem, event) {
  event.preventDefault()
  elem.scrollLeft += event.deltaX
  elem.scrollTop += event.deltaY
}

// Less precise than the pixel version. Breaks when the event has BOTH scrollX
// and scrollY. Should work fine for regular mouse wheels.
function scrollWithoutSpillModeLinesOrPages(elem, event) {
  const movesLeft = event.deltaX < 0 && elem.scrollLeft > 0
  const movesRight = event.deltaX > 0 && elem.scrollLeft + elem.clientWidth < elem.scrollWidth
  const movesUp = event.deltaY < 0 && elem.scrollTop > 0
  const movesDown = event.deltaY > 0 && elem.scrollTop + elem.clientHeight < elem.scrollHeight
  if (!movesLeft && !movesRight && !movesUp && !movesDown) event.preventDefault()
}

/*
Reference: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView

Browser support: noop in IE.
*/
export function scrollIntoView(elem, opts) {
  if (!c.DOM_AVAILABLE || !a.optElement(elem)) return
  if (elem.scrollIntoView) elem.scrollIntoView(opts)
  else window.scrollTo(window.scrollX, elem.offsetTop)
}

export function scrollIntoViewBottom(elem) {
  if (!c.DOM_AVAILABLE || !a.optElement(elem)) return
  const diff = elem.getBoundingClientRect().bottom - window.innerHeight
  if (!(diff > 0)) return
  window.scrollBy(0, diff)
}

/*
Takes a container (possibly scrollable and scrolled to some X and Y offsets),
and an element inside that container, and determines their edges and
intersections on X and Y.

Each of the "bounded" coordinates may be an intersection between the element and
the viewport, the corresponding element edge within the viewport, or the
corresponding viewport edge if the element doesn't fit in the viewport on this
dimension.

1D examples:

    C ************++++++++++++************
                  ↑ viewport ↑

    E ---******---------------------------
                  ↑ bounded left
                  ↑ bounded right

    E ---------******---------------------
                  ↑ bounded left
                    ↑ bounded right

    E ---------------******---------------
                     ↑ bounded left
                          ↑ bounded right

    E ---------------------******---------
                           ↑ bounded left
                              ↑ bounded right

    E ---------------------------******---
                              ↑ bounded left
                              ↑ bounded right

TODO consolidate with `getBoundingClientRectVisible`, generalizing the latter to
allow specifying an explicit container or the window viewport. This function
uses APIs limited to pixel precision (integers), while functions based on
`getBoundingClientRect` have sub-pixel precision (floats).
*/
export function elemBoundsInContainer(cont, elem) {
  a.reqElement(cont)
  a.reqElement(elem)

  const viewportLeft = cont.scrollLeft
  const viewportRight = cont.scrollLeft + cont.clientWidth
  const viewportTop = cont.scrollTop
  const viewportBottom = cont.scrollTop + cont.clientHeight

  const elemLeft = elem.offsetLeft
  const elemRight = elem.offsetLeft + elem.clientWidth
  const elemTop = elem.offsetTop
  const elemBottom = elem.offsetTop + elem.clientHeight

  const boundedLeft = Math.max(viewportLeft, Math.min(viewportRight, elemLeft))
  const boundedRight = Math.max(viewportLeft, Math.min(viewportRight, elemRight))
  const boundedTop = Math.max(viewportTop, Math.min(viewportBottom, elemTop))
  const boundedBottom = Math.max(viewportTop, Math.min(viewportBottom, elemBottom))

  return {
    viewportLeft, viewportRight, viewportTop, viewportBottom,
    elemLeft,     elemRight,     elemTop,     elemBottom,
    boundedLeft,  boundedRight,  boundedTop,  boundedBottom,
  }
}

export function widthPixelsVisibleInContainer(cont, elem) {
  const {boundedLeft, boundedRight} = elemBoundsInContainer(cont, elem)
  return uma.toNat(boundedRight - boundedLeft)
}

export function widthPixelsHiddenInContainer(cont, elem) {
  const {elemLeft, elemRight, boundedLeft, boundedRight} = elemBoundsInContainer(cont, elem)
  const hiddenLeft = uma.toNat(boundedLeft - elemLeft)
  const hiddenRight = uma.toNat(elemRight - boundedRight)
  return hiddenLeft + hiddenRight
}

// See `widthPixelsVisibleInContainer` for a graphic example.
export function heightPixelsVisibleInContainer(cont, elem) {
  const {boundedTop, boundedBottom} = elemBoundsInContainer(cont, elem)
  return uma.toNat(boundedBottom - boundedTop)
}

// See `widthPixelsVisibleInContainer` for a graphic example.
export function heightPixelsHiddenInContainer(cont, elem) {
  const {elemTop, elemBottom, boundedTop, boundedBottom} = elemBoundsInContainer(cont, elem)
  const hiddenTop = uma.toNat(boundedTop - elemTop)
  const hiddenBottom = uma.toNat(elemBottom - boundedBottom)
  return hiddenTop + hiddenBottom
}

export function widthKindaFitsInContainer(cont, elem) {
  return widthPixelsHiddenInContainer(cont, elem) <= c.PIXEL_ERROR_MARGIN
}

export function heightKindaFitsInContainer(cont, elem) {
  return heightPixelsHiddenInContainer(cont, elem) <= c.PIXEL_ERROR_MARGIN
}

export function isKindaWithinViewport(elem) {
  if (a.isNil(elem)) return false
  a.reqElement(elem)
  const {top, bottom} = elem.getBoundingClientRect()
  return (
    (bottom > c.PIXEL_ERROR_MARGIN && bottom < window.innerHeight) ||
    (top > c.PIXEL_ERROR_MARGIN && top < (window.innerHeight - c.PIXEL_ERROR_MARGIN))
  )
}

export function isAreaPortionWithinViewport(elem, portion) {
  a.reqFin(portion)
  if (a.isNil(elem)) return false
  a.reqElement(elem)

  const total = getBoundingClientRect(elem)
  const visible = getBoundingClientRectVisible(elem)
  const totalArea = total.width * total.height
  const visibleArea = visible.width * visible.height
  return visibleArea >= (totalArea * portion)
}

/*
Registers a callback that will be called with the previous and next focused
elements on any focus change. Returns a cleanup function.

Handling blur and focus is tricky due to multiple possible event combinations:

  (1) blur

      we must wait to see if there's a focus event, then notify with:
      blurred = event target, focused = document.activeElement

  (2) blur + focus

      after blur event, we must wait until focus event, then notify with:
      blurred = blur event target, focused = focus event target

  (3) focus

      if there was a recent blur event, see (2)
      otherwise we must notify with:
      blurred = document.activeElement, focused = event target

This function handles all of them for you.
*/
export function onGlobalFocusChange(fun) {
  a.reqFun(fun)

  let recentBlurred = undefined
  let waiting = undefined

  function wait(callback) {
    unwait()
    waiting = requestAnimationFrame(callback)
  }

  function unwait() {
    if (waiting) cancelAnimationFrame(waiting)
    waiting = undefined
  }

  function blurNotify() {
    const blurred = recentBlurred
    recentBlurred = undefined
    fun(blurred, undefined)
  }

  function focusNotify(focused) {
    const blurred = recentBlurred
    recentBlurred = undefined
    fun(blurred || undefined, focused)
  }

  const unblur = addEvent(window, `blur`, function onBlur({target}) {
    recentBlurred = target
    wait(blurNotify)
  }, CAPTURE)

  const unfocus = addEvent(window, `focus`, function onFocus({target}) {
    unwait()
    focusNotify(target)
  }, CAPTURE)

  return function deinit() {
    unwait()
    unblur()
    unfocus()
    recentBlurred = undefined
  }
}

/*
Same as `elem.getBoundingClientRect` but returns `{}` when the element is
missing or the API is not available. In this case, calculations in the calling
function will resolve to NaN. The caller should expect this and handle it
gracefully. This behavior should make it safe in unsupported browsers, if any.

TODO drop because we don't support browsers where this is missing.
*/
export function getBoundingClientRect(elem) {
  if (a.isNil(elem)) return {}
  a.reqElement(elem)
  if (elem.getBoundingClientRect) return elem.getBoundingClientRect()
  return {}
}

/*
Similar to `getBoundingClientRect` but restricts `top, right, bottom, left,
width, height` to only the portion that's within the viewport.

TODO consolidate with `elemBoundsInContainer` and allow to choose the container.

Known issue: `window.innerHeight` and `window.innerWidth` appear to be limited
to integer pixel precision. TODO see if we can replace them with an API that has
sub-pixel precision.
*/
export function getBoundingClientRectVisible(elem) {
  if (a.isNil(elem)) return {}
  const rect = getBoundingClientRect(elem)
  const top = uma.toNat(rect.top)
  const bottom = Math.min(window.innerHeight, rect.bottom)
  const left = uma.toNat(rect.left)
  const right = Math.min(window.innerWidth, rect.right)
  const width = right - left
  const height = bottom - top
  return {top, bottom, left, right, width, height}
}

export function getClientX(event) {
  return event.type === `touchstart` || event.type === `touchmove`
    ? event.changedTouches?.[0]?.clientX
    : a.laxStruct(event).clientX
}

export function hasWidthOverflow(val) {
  return a.isElement(val) && val.scrollWidth > val.getBoundingClientRect()?.width
}

export function hasHeightOverflow(val) {
  return a.isElement(val) && val.scrollHeight > val.getBoundingClientRect()?.height
}

export async function mountStyle(id, src) {
  a.reqValidStr(id)
  a.reqValidStr(src)

  if (!c.DOM_AVAILABLE) return undefined

  return elemById(id) || (await nodePromise(
    document.head.appendChild(
      uv.ren.elemHtml(`link`, {id, rel: `stylesheet`, href: src}),
    )
  )).target
}

export async function mountScript(id, src) {
  a.reqValidStr(id)
  a.reqValidStr(src)

  if (!c.DOM_AVAILABLE) return undefined

  return elemById(id) || (await nodePromise(
    document.head.appendChild(
      uv.ren.elemHtml(`script`, {id, src, async: true}),
    )
  )).target
}

/*
Caution. Rules for using this:

  * The node must actually dispatch events `load` and `error`. In other words,
    the node must natively fetch a remote resource.

  * Can be used only before adding the node to the DOM, which typically triggers
    the fetching. For already-loaded nodes, this would hang forever.
*/
function nodePromise(tar) {
  a.reqNode(tar)

  return new Promise(function initNodePromise(done, fail) {
    tar.addEventListener(`load`, done, {once: true})
    tar.addEventListener(`error`, fail, {once: true})
  })
}

export async function mountGoogleMapsScript() {
  if (!c.GOOGLE_MAPS_ENABLED) return undefined
  return mountScript(`google-maps`, c.GOOGLE_MAPS_SCRIPT_URL)
}

// Daily is a 3rd party streaming service
export function mountDaily() {
  if (!c.DAILY_ENABLED) return undefined
  return mountScript(`daily-meet`, c.DAILY_SCRIPT_URL)
}

// Unused, TODO remove. See our own `VimeoPlayer`.
//
// SDK sources: `https://github.com/vimeo/player.js`.
export function mountVimeoSdk() {
  if (!c.VIMEO_ENABLED) return undefined
  return mountScript(`vimeo-sdk`, c.VIMEO_SCRIPT_URL)
}

export function mountTypeform() {
  if (!c.TYPEFORM_ENABLED) return undefined
  return mountScript(`typeform`, c.TYPEFORM_SCRIPT_URL)
}

export async function useTypeform() {
  if (!c.TYPEFORM_ENABLED) return undefined
  await mountTypeform()
  return window[`embed-next`]
}

export function isFileList(val) {
  return typeof FileList === `function` && a.isInst(val, FileList)
}

export function removeFiles(val) {return a.reject(val, a.isFile)}

export function isFullscreen() {
  return (
    document.fullScreen ||
    document.mozFullScreen ||
    document.webkitIsFullScreen
  )
}

export function geolocationGetCurrentPosition(opts) {
  return new Promise(function geoInit(done, fail) {
    window.navigator.geolocation.getCurrentPosition(done, fail, opts)
  }).catch(skipGeoErr)
}

function skipGeoErr(err) {
  const cls = globalThis.GeolocationPositionError
  if (cls && a.isInst(err, cls)) return undefined
  throw err
}

export function simulateActivationOnKeypress(onClick, event) {
  a.reqFun(onClick)
  if (event.keyCode === c.KEY_CODE_ENTER) {
    onClick(event)
    return
  }

  // Simulate on-Space button activation and prevent default scrolling.
  if (event.keyCode === c.KEY_CODE_SPACE) {
    event.preventDefault()
    onClick(event)
  }
}

/*
Note: doesn't include special support for smooth scroll, which can be done
purely in CSS with `scroll-behavior: smooth`.

TODO: consider if modifying `history.scrollRestoration` could be useful.
*/
export function scrollOnLocationChange(prev, next, isPop) {
  if (!next) return

  const id = ut.hashToId(next.hash)
  if (id) {
    scrollYToId(id)
    return
  }

  if (!prev) return

  if (isPop) {
    // The browser should automatically restore the scroll position.
    // We stimulate a layout recalculation to make this more reliable.
    forceLayoutHeight()
    return
  }

  if (prev.pathname === next.pathname && prev.search !== next.search) {
    return
  }

  scrollToTop()
}

function forceLayoutHeight() {
  // Stimulate a height measurement to improve the chances of restoring the
  // scroll position when navigating back and forward. This must be done after
  // re-rendering after a route transition.
  document.body.scrollHeight  // eslint-disable-line no-unused-expressions
}

// Accepts location dicts, instances of URL, `window.location`, etc.
export function isLocHashChange(prev, next) {
  prev = a.laxStruct(prev)
  next = a.laxStruct(next)

  return (
    prev.pathname === next.pathname &&
    prev.search === next.search &&
    prev.hash !== next.hash &&

    /*
    Caution. This condition exists because of a gotcha in the default browser
    behavior. When the only difference between the prev and next locations is
    the hash, and the prev location has a hash, and the href on the anchor for
    the next location does not have a `#` symbol, then the browser performs a
    full page transition, instead of a hash / pushstate transition. Meanwhile,
    when the href on the anchor for the next location ENDS with a `#` symbol,
    then the browser performs a hash / pushstate transition. And in both cases,
    despite the different behavior, `next.hash` is an empty string. We should
    carefully match the default browser behavior, performing our own pushstate
    transition exactly in the same cases where the browser performs a regular
    document transition for the current page.
    */
    !!next.hash && !next.href.endsWith(`#`)
  )
}

export function formEnable(form) {
  a.reqInst(form, HTMLFormElement)
  a.each(form.elements, enableAfterSsr)
}

export function isDisablable(val) {
  return a.optObj(val) && `disabled` in val && a.isBool(val.disabled)
}

export function disable(node) {
  if (isDisablable(node) && !node.disabled) node.disabled = true
}

export function enable(node) {
  if (isDisablable(node) && node.disabled) node.disabled = false
}

// TODO: disable only fieldsets instead of actual controls.
export function enableAfterSsr(node) {
  if (a.optElement(node) && `enable` in node.dataset) enable(node)
}

export function isHidable(val) {
  return a.optElement(val) && `hidden` in val && a.isBool(val.hidden)
}

export function isHidden(val) {return isHidable(val) && !!val.hidden}
export function isNotHidden(val) {return isHidable(val) && !val.hidden}

export function hasNotHiddenChildNodes(src) {
  return a.some(src?.childNodes, isNotHidden)
}

export function hide(node) {
  if (isHidable(node) && !node.hidden) node.hidden = true
}

export function show(node) {
  if (isHidable(node) && node.hidden) node.hidden = false
}

// TODO better name.
export function syncHiddenWithChildren(tar) {
  if (a.optElement(tar)) tar.hidden = !hasNotHiddenChildNodes(tar)
}

export function selCls(cls) {
  reqElemCls(cls)
  const localName = a.reqValidStr(cls.localName)
  const src = document.querySelectorAll(localName)
  if (localName === cls.customName) return src
  return a.filter(src, function test(val) {return val.constructor === cls})
}

// TODO better name.
export function sel(node, val) {
  a.reqNode(node)
  if (a.isNil(val) || !a.optStr(val)) return null
  return node.querySelector(val)
}

// TODO better name.
export function selAll(node, val) {
  a.reqNode(node)
  if (a.isNil(val) || !a.optStr(val)) return []
  return node.querySelectorAll(val)
}

export function elemById(id) {
  if (!a.laxStr(id) || !c.DOM_AVAILABLE) return undefined
  return document.getElementById(id)
}

export function elemShow(node) {
  if (a.optElement(node) && node.hidden) node.hidden = false
}

export function elemHide(node) {
  if (a.optElement(node) && !node.hidden) node.hidden = true
}

export function elemToggleHidden(elem, hidden) {
  return (
    a.optElement(elem) &&
    (elem.hidden = a.optBool(hidden) ?? !elem.hidden)
  )
}

export function eachSel(sel, fun, ...args) {
  a.reqFun(fun)
  for (const node of selAll(document, sel)) fun(node, ...args)
}

export function dispatch(node, type, detail) {
  node.dispatchEvent(customEvent(type, detail))
}

/*
Useful for emulating native change events in custom elements that change the
state of form controls in other ways, for example by removing inputs.
*/
export function dispatchChange(node) {
  node.dispatchEvent(new Event(`change`, {bubbles: true}))
}

export function dispatchErr(node, err) {
  node.dispatchEvent(errorEvent(err))
}

export function customEvent(type, detail) {
  return new CustomEvent(type, {bubbles: true, detail})
}

export function errorEvent(err) {
  return new ErrorEvent(`error`, {
    bubbles: true,
    error: err,
    message: err ? err.message : ``,
  })
}

export function isElemWithTag(val, tag) {
  return a.isElement(val) && val.tagName === tag
}

export function isBtnWithType(val, type) {
  return isElemWithTag(val, `BUTTON`) && val.type === type
}

export function isSubmitBtn(val) {return isBtnWithType(val, `submit`)}
export function isButtonBtn(val) {return isBtnWithType(val, `button`)}

export function isInput(val) {return isElemWithTag(val, `INPUT`)}
export function isRadioInput(val) {return isInput(val) && val.type === `radio`}
export function isNonRadioInput(val) {return isInput(val) && val.type !== `radio`}
export function isCheckboxInput(val) {return isInput(val) && val.type === `checkbox`}

/*
TODO better detection. The well-known element `summary` does NOT have an
associated constructor class. It's instantiated as `HTMLElement`.
*/
export function isSummary(val) {return isElemWithTag(val, `SUMMARY`)}

export function hasAttr(node, key) {
  if (a.isNil(node)) return false
  a.reqNode(node)
  return a.isElement(node) && node.hasAttribute(key)
}

export function hasAttrVal(node, key, val) {
  if (a.isNil(node)) return false
  a.reqNode(node)
  return a.isElement(node) && node.hasAttribute(key) && node.getAttribute(key) === val
}

// https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL
export class FileUrl extends String {
  constructor(val) {super(a.onlyStr(val) ?? URL.createObjectURL(val))}
  deinit() {revokeObjectUrl(this.valueOf())}
  static opt(val) {return (val = createObjectUrl(val)) && new this(val)}
}

// Note: the returned URL string must be later revoked via `URL.revokeObjectURL`
// to avoid memory leakage.
export function createObjectUrl(val) {
  return a.isFile(val) ? URL.createObjectURL(val) : undefined
}

export function revokeObjectUrl(val) {
  if (a.optStr(val)) URL.revokeObjectURL(val)
}

// TODO:
//   * Attempt to respect `tabIndex` order.
export function focusFirst(elem) {
  const node = (
    sel(elem, `[autofocus]`) ||
    sel(elem, `[data-autofocus]`) ||
    sel(elem, `button`) ||
    sel(elem, `[role=button]`) ||
    sel(elem, `summary`)
  )
  if (node && a.isFun(node.focus)) node.focus()
}

/*
This probably doesn't work in the DOM shim, so the behavior might be
inconsistent between SSR and browser. TODO review.
*/
export function unescape(src) {
  src = a.laxStr(src)
  return src && (uv.ren.makeElemHtml(`div`).innerHTML = src)
}

// TODO better name.
export function strNorm(val) {return unescape(ut.strTrim(val))}

/*
Mount ReCAPTCHA v3 script:
https://developers.google.com/recaptcha/docs/invisible#example
*/
export function mountRecaptchaScript() {
  if (!c.RECAPTCHA_ENABLED) return undefined

  if (!c.RECAPTCHA_SCRIPT_URL) {
    return Promise.reject(Error(`can't mount recaptcha script: missing RECAPTCHA_SCRIPT_URL`))
  }

  const url = ut.url(c.RECAPTCHA_SCRIPT_URL).setQuery({render: c.RECAPTCHA_SITE_KEY})
  return mountScript(`recaptcha`, url)
}

export function isRecaptchaAvailable() {
  return c.DOM_AVAILABLE && a.isObj(window.grecaptcha)
}

export function resizeTextarea(elem, {minRows, maxRows} = {}) {
  a.reqElement(elem)

  minRows = a.laxNat(minRows) || 2
  maxRows = a.laxNat(maxRows) || 64

  elem.rows = minRows
  while (elem.scrollHeight > elem.clientHeight && elem.rows <= maxRows) {
    elem.rows += 1
  }
  return elem
}

export function ensureElem(id) {
  a.reqStr(id)

  return (
    elemById(id) ||
    document.body.appendChild(uv.ren.elemHtml(`div`, {id}))
  )
}

export function onExactEventTarget(fun) {
  a.reqFun(fun)

  return function onExactEventTarget_(event) {
    // eslint-disable-next-line no-invalid-this
    if (event.target === this) fun(event)
  }
}

export function maybeVisCount(opts) {
  return c.INTERSECTION_AVAILABLE ? new VisCount(opts) : undefined
}

/*
Short for "visibility counter".
Uses `IntersectionObserver` to track visibility of multiple elements.
The observable property `.size` shows how many are currently visible.

Consumer code MUST explicitly add and remove DOM nodes to the observer:

  class SomeElem extends u.Elem {
    connectedCallback() {visObs.observe(this)}
    disconnectedCallback() {visObs.unobserve(this)}
  }
*/
export class VisCount extends ob.DeObs {
  constructor(opts = {threshold: [1]}) {
    super()
    this.size = 0
    this.set = new Set()
    this.obs = new IntersectionObserver(this.onIntersect.bind(this), opts)
  }

  refresh() {this.size = a.laxNat(this.set.size)}

  onIntersect(entries) {
    for (const {target, isIntersecting} of entries) {
      if (isIntersecting) this.set.add(target)
      else this.set.delete(target)
    }
    this.refresh()
  }

  deinit() {
    this.obs.disconnect()
    this.set.clear()
    ob.self(this).refresh()
  }
}

export function scrollTo(id) {elemById(id)?.scrollIntoView()}

export function historyPush(val) {
  const {history} = window
  history.pushState(history.state, ``, a.render(val))
}

export function historyReplace(val) {
  const {history} = window
  history.replaceState(history.state, ``, a.render(val))
}

export function hardRedirect(link) {window.location.href = a.render(link)}

// TODO better place for this function.
export function locPushPromoNag(ctx, link) {
  historyPush(link)
  return ctx.env.locPush(c.LINK_PLANS)
}

export function appendSibling(tar, val) {
  a.reqNode(tar)
  if (a.optNode(val)) tar.parentNode.insertBefore(val, tar.nextSibling)
  return val
}

export function prependSibling(tar, val) {
  a.reqNode(tar)
  if (a.optNode(val)) tar.parentNode.insertBefore(val, tar)
  return val
}

export function prependChild(tar, val) {
  a.reqNode(tar)
  if (a.optNode(val)) tar.prepend(val)
  return val
}

export function removeNodes(val) {a.each(val, removeNode)}
export function removeNode(val) {a.optNode(val)?.remove()}

/*
Conditional focusing. Avoid stealing focus if the currently-focused element
is one of the native DOM controls that support keyboard inputs. Otherwise
focus the given element.
*/
export function maybeFocus(val) {
  return (
    !!a.optElement(val) &&
    !hasAncestor(document.activeElement, isElementWithKeydownEvent) &&
    (val.focus({preventScroll: true}), true)
  )
}

export function isEventModified(val) {
  return !!(a.optEvent(val) && (
    val.altKey || val.ctrlKey || val.metaKey || val.shiftKey
  ))
}

export function isEventModifiedPrimary(val) {
  return !!(a.optEvent(val) && (val.ctrlKey || val.metaKey))
}

export function isEventKeydownSubmit(val) {
  return (
    isEventModifiedPrimary(val) &&
    val.key === c.KEY_ENTER
  )
}

/*
Allows to detect "normal" interactions such as typing in an input or clicking a
link. Useful for avoiding redundant hotkey activation.

Ideally, we would prefer to detect default event handling with something like
`eve.defaultPrevented`. Unfortunately, this wouldn't work for two reasons.
First, default event handling on elements such as links or buttons doesn't
perform `.preventDefault()`. Second, default handling runs AFTER all custom
event listeners.

Too much special-case knowledge. TODO avoid.
*/
export function eventHasDefault(eve, top) {
  a.optEvent(eve)
  a.optElement(top)
  if (!eve) return false

  if (eve.type === `input`) return true
  if (eve.type === `change`) return true

  let tar = eve.target
  while (tar) {
    if (tar === top) return false
    if (eve.type === `click` && isElementWithClickEvent(tar)) return true
    if (eve.key === c.KEY_ENTER && isElementWithEnterEvent(tar)) return true
    if (eve.key === c.KEY_SPACE && isElementWithSpaceEvent(tar)) return true
    if (eve.type === `keydown` && isElementWithKeydownEvent(tar)) return true
    tar = tar.parentNode
  }
  return false
}

function isElementWithClickEvent(val) {
  return (
    a.isInst(val, HTMLAnchorElement) ||
    a.isInst(val, HTMLInputElement) ||
    a.isInst(val, HTMLTextAreaElement) ||
    a.isInst(val, HTMLButtonElement) ||
    a.isInst(val, HTMLSelectElement) ||
    isInteractiveLabelElement(val)
  )
}

function isElementWithEnterEvent(val) {
  return a.isObj(val) && !!(
    a.isInst(val, HTMLAnchorElement) ||
    a.isInst(val, HTMLFormElement) ||
    a.isInst(val, HTMLTextAreaElement) ||
    a.isInst(val, HTMLButtonElement) ||
    a.isInst(val, HTMLSelectElement)
  )
}

function isElementWithSpaceEvent(val) {
  return (
    a.isInst(val, HTMLButtonElement) ||
    a.isInst(val, HTMLSelectElement)
  )
}

function isElementWithKeydownEvent(val) {
  return (
    isTextInput(val) ||
    a.isInst(val, HTMLSelectElement)
  )
}

export function isInteractiveLabelElement(val) {
  return a.isInst(val, HTMLLabelElement) && !!val.htmlFor
}

export function isEventInsideTextInput(val) {
  return !!(
    a.optEvent(val) &&
    a.findAncestor(val.target, isTextInput)
  )
}

function isTextInput(val) {
  return (
    a.isInst(val, HTMLInputElement) ||
    a.isInst(val, HTMLTextAreaElement)
  )
}

/*
Similar to using `window.addEventListener`, but runs listeners backwards. In the
standard DOM implementation of `EventTarget..addEventListener`, listeners are
invoked in the FIFO order. However, for global listeners, we often want the
LIFO order, where later listeners take priority over earlier ones. For example,
when opening a modal that gets closed on Esc, we want its listener to take
priority over any other Esc handlers that previously existed.

Unlike `EventTarget`, this doesn't implement `.removeEventListener` which is
error prone. Many developers fail to actually remove listeners, by passing the
wrong references to `.removeEventListener`. To minimize errors, each call to
`.addEvent` returns a cleanup function which must be stored by the caller and
called when deiniting.
*/
export class EventLifo extends a.Emp {
  constructor() {
    super()
    this.sets = Object.create(null)
    // Workaround for evil 3rd party scripts.
    // See `readme.md` → gotchas → event listeners.
    this.handleEvent = this.handleEvent.bind(this)
  }

  addEvent(type, han) {
    a.reqValidStr(type)
    a.req(han, isValidEventHandler)

    const set = this.sets[type] ??= new ucc.ReverseSet()
    const fun = this.handleEvent
    const emp = !set.size
    set.add(han)
    if (emp) window.addEventListener(type, fun)

    return function removeEvent() {
      set.delete(han)
      if (!set.size) window.removeEventListener(type, fun)
    }
  }

  deinit() {
    for (const [key, val] of a.entries(this.sets)) {
      val?.clear()
      window.removeEventListener(key, this.handleEvent)
    }
  }

  handleEvent(eve) {
    const type = a.laxStr(eve?.type)
    const set = this.sets[type]
    if (!set) return

    for (const han of set) {
      if (a.isFun(han)) han(eve)
      else han.handleEvent(eve)
      if (eve.defaultPrevented) return
    }
  }
}

function isValidEventHandler(val) {
  return a.isFun(val) || (a.isObj(val) && `handleEvent` in val)
}

// SYNC[css_flash].
const CLS_FLASH = `flash`

/*
SYNC[css_flash].

If the CSS class uses a CSS animation, this must use `onanimationend`.
If the CSS class uses a CSS transition, this must use `ontransitionend`.
*/
export function elemFlash(tar) {
  if (a.optElement(tar) && c.DOM_AVAILABLE) {
    tar.classList.add(CLS_FLASH)
    tar.onanimationend = selfElemUnflash
  }
  return tar
}

export function elemUnflash(tar) {
  if (a.optElement(tar) && c.DOM_AVAILABLE) {
    tar.classList.remove(CLS_FLASH)
    tar.onanimationend = null
  }
  return tar
}

export function elemFlashToggle(tar, ok) {
  if (ok) return elemFlash(tar)
  return elemUnflash(tar)
}

// eslint-disable-next-line no-invalid-this
function selfElemUnflash() {elemUnflash(this)}

export function clickNotHidden(elem) {
  if (!a.optElement(elem) || elem.hidden) return
  elem.scrollIntoView()
  elem.click()
}

/*
Native checkbox inputs have a fallback value of "on". When the attribute "value"
is unset, the getter `.value` returns "on" by default. This function bypasses
the fallback by reading only the attribute.

This is only relevant for multiselect checkboxes. On boolean checkboxes `.value`
must be unset, and we must use `.checked` and `.indeterminate`.
*/
export function checkboxValue(val) {
  return a.optStr(a.optElement(val)?.getAttribute(`value`))
}

export function checkedCheckboxValue(val) {
  return a.optElement(val)?.checked ? checkboxValue(val) : undefined
}

export function fileFormData(val) {
  const out = new FormData()
  // This field name is meaningless. Our server technically supports multiple
  // files per multipart request, but we prefer one request per file.
  out.append(`file`, a.reqFile(val))
  return out
}

export function isFormData(val) {return a.isInst(val, FormData)}
export function reqFormData(val) {return a.reqInst(val, FormData)}
export function optFormData(val) {return a.optInst(val, FormData)}

export function requestAnimationFramePromise() {
  return new Promise(function requestAnimationFramePromiseInit(done) {
    requestAnimationFrame(done)
  })
}

export function isCurrent(src) {
  if (a.hasMeth(src, `isCurrent`)) return !!src.isCurrent()

  /*
  Reference:

    https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-current
  */
  const val = a.isElement(src) ? src.getAttribute(`aria-current`) : undefined
  return !!val && val !== `false`
}

/*
Intended for code that runs in `.connectedCallback` and needs to check if
specific ancestors, descendants, or other related elements have been
instantiated as custom elements yet. Note that when registering a custom
element class, `.connectedCallback` runs SYNCHRONOUSLY. This means when you
register classes `A` and `B` one after another, `A..connectedCallback` runs
BEFORE registering class `B`. Because we place custom element class definitions
in multiple files, the order of their instantiation is basically undefined.

This implementation works only for custom element classes registered via
`dr.reg`. Direct use of `customElements.define` may bypass this, unless
the constructor defines `.customName`.

TODO: better approach; find built-in API.
*/
export function isElemCustom(val) {
  a.optElement(val)
  return !!val?.constructor?.customName
}

export function isElemCls(val) {
  return a.isCls(val) && `nodeType` in val.prototype
}

export function reqElemCls(val) {return a.req(val, isElemCls)}

/*
This is satisfied by custom element classes with `static {dr.reg(this)}`.
The custom element registration system assigns the non-standard property
`.localName` to element classes when registering them.
*/
export function isElemClsRegistered(val) {
  return isElemCls(val) && !!val.localName
}

/*
`@mitranim/js` has this in `dom_shim.mjs`, but not in `dom.mjs`.
We don't want to import `dom_shim.mjs` in the client bundle.
*/
export function isComment(val) {
  return a.isNode(val) && val.nodeType === Node.COMMENT_NODE
}
export function reqComment(val) {return a.req(val, isComment)}
export function optComment(val) {return a.opt(val, isComment)}

export function nodeOnlyNotEmpty(val) {
  return a.vac(a.optNode(val)?.hasChildNodes()) && val
}

export function copyToClipboard(val) {
  val = a.render(val)
  const prev = document.activeElement
  try {return a.clip(val)}
  finally {prev?.focus?.()}
}
